import {
  SET_RECIBOS,
  SET_CARGOS_PUBLICO_GENERAL,
  SET_LAST_PAYMENT_DATA,
  SET_RECIBOS_ORDEN,
  SET_RECIBO_CONVENIO,
  SET_LAST_PAYMENT_DATA_CONVENIO,
  // SET_RECIBO_CONVENIO,
} from 'store/reducers/caja';
import { store } from 'store';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import currencyFormater from 'utils/toCurrency';
import getFormattedValues, {
  discartNullValues,
  formatReceived,
  hasFiles,
} from 'utils/formatValues';
import { getContentTypesPadrones } from 'api/catalogos';
import { message } from 'antd';
import { getConsultaTask } from 'api/utils/consultaTask';
import padronToString from 'utils/padronesToString';
import { getTipoDePadronPorContenType } from 'utils/padrones';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';

const { estadosGlobales, padrones } = store.getState().catalogs;
export const baseURI = 'recaudacion/recibos/';
const ENV_NAME = process.env.REACT_APP_ENV_NAME;
const files = ['archivo'];

/**
 * GET Recibos
 * @returns {Promise<Object>}
 */
export const getRecibos = async (params = null) => {
  try {
    const response = await API.get(baseURI, { params });
    return response.data;
  } catch (err) {
    onError(err);
    return { results: [] };
  }
};

/**
 * GET Recibos
 * @param {object} values Id de recibo
 * @returns {Promise<Object>}
 */
export const getReciboPorFolio = async (values = null) => {
  try {
    const response = await API
      .post('recaudacion/recibo/consultar-recibo-por-folio', discartNullValues(values));
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Recibos por padrones
 * @returns {Promise<Array<Object>>}
 */
export const getRecibosPorPadrones = async (sales = []) => {
  try {
    const promises = sales.map((data) => API.post('recaudacion/consultar-recibos-por-cargo/', {
      data: [data],
    }));
    const responses = await Promise.all(promises);
    const response = responses.reduce((acum, curr) => [...acum, ...curr.data], []);

    store.dispatch({
      type: SET_RECIBOS,
      payload: response.map((e) => e?.id || e),
    });
    const cargosPagados = sales.reduce((acum, curr) => [...acum, ...curr.cargos], []);
    const { cargosPublicoGeneral } = store.getState().caja;
    store.dispatch({
      type: SET_CARGOS_PUBLICO_GENERAL,
      payload: cargosPublicoGeneral.filter((e) => !cargosPagados.includes((e))),
    });
    return response;
  } catch (err) {
    message.info('No existen recibos para la lista de cargos cargos proporcionados');
  }
  return [];
};

/**
 * GET Recibo
 * @param {number} id Id de recibo
 * @returns {Promise<Object|boolean>}
 */
export const getRecibo = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Recibo PDF
 * @param {number} id Id de recibo
 * @param {string} observacion Valores de recibo
 * @returns {Promise<Object|boolean>}
 */
export const patchObservaciones = async (id, observaciones) => {
  try {
    const response = await API.patch(`${baseURI}${id}/`, { observaciones });
    onSuccess(null, 'Observaciones añadidas correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Recibo
 * @param {number} id Id de recibo
 * @param {object} values Valores de recibo
 * @param {string} _message Mensaje de feedback
 * @returns {Promise<Object|boolean>}
 */
export const patchRecibo = async (id, values, _message = 'Actualizado Correctamente') => {
  try {
    const response = await API.patch(`${baseURI}${id}/`, values);
    onSuccess(response, _message);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Recibo
 * @param {number} id Id de recibo
 * @param {object} values Valores de recibo
 * @returns {Promise<Object|boolean>}
 */
export const postCancelarRecibo = async (id, values) => {
  try {
    const response = await API.post(`${baseURI}${id}/cancelar/`, values);
    onSuccess(response, 'Cancelado Correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Generar recibos
 * @param {Object} values
 * @returns {Promise<boolean|Array>}
 */
export const postRecibo = async (_values) => {
  const values = { ..._values };
  if (values?.padrones?.length) {
    values.idPadron = values.padrones[0].tipo_de_padron;
  }
  values.accionInf = 'GENERAR_RECIBO_CAJA';
  store.dispatch({
    type: SET_LAST_PAYMENT_DATA,
    payload: values.padrones,
  });
  const defMessage = 'Saldando Cargos...';
  const [recibos] = await getConsultaTask(
    values,
    baseURI,
    defMessage,
    'recibos',
    [],
    true,
    null,
  );
  if (recibos.length) {
    store.dispatch({
      type: SET_RECIBOS,
      payload: recibos,
    });
    const cargosPagados = values.padrones
      .reduce((acum, curr) => [...acum, ...curr.cargos], []);
    const { cargosPublicoGeneral = [] } = store.getState().caja;
    store.dispatch({
      type: SET_CARGOS_PUBLICO_GENERAL,
      payload: cargosPublicoGeneral.filter((e) => !cargosPagados.includes((e))),
    });
    return recibos;
  }
  store.dispatch({
    type: SET_LAST_PAYMENT_DATA,
    payload: null,
  });
  return [];
};

/**
 * POST Archivo de pago externos en registro de pagos
 * @param {object} values File
 * @returns {Promise<Object|boolean>}
 */
export const postFilePagoExterno = async (values) => {
  try {
    const config = hasFiles(values, files) ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    const formattedValues = getFormattedValues(values, {
      files,
      formData: config.headers,
      clean: false,
    });
    const response = await API.post('/recaudacion/recibo/generar-recibo-por-referencia-de-pago/return-file-hex', formattedValues, config);
    onSuccess(response, 'Archivo leido correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
/**
 * POST Generar recibos con folio de orden de pago
 * @param {Object} values
 * @returns {Promise<boolean|Array>}
 */
export const postReciboConFolioDeOrden = async (values) => {
  try {
    const _values = getFormattedValues(values);
    _values.accionInf = 'GENERAR_RECIBO_CAJA';
    const response = await API
      .post('/recaudacion/recibo/generar-recibo-por-referencia-de-pago/generar-recibo/', _values);
    store.dispatch({
      type: SET_RECIBOS_ORDEN,
      payload: response.data.recibos.map((e) => e.id),
    });
    return response.data.recibos.length;
  } catch (err) {
    onError(err);
    return false;
  }
};

const formatValues = (recibo) => {
  const { padron, canal_de_pago } = recibo;
  const cajero = recibo.corte?.usuario_cajero;
  const numEmpleado = cajero?.numero_de_empleado || cajero?.id;
  const nombreEmpleado = `${cajero?.first_name} ${cajero?.last_name} ${cajero?.second_last_name || ''}`;
  const tipoDePadron = getTipoDePadronPorContenType(recibo.content_type_padron);
  const [clave_padron, ...rest] = padronToString(padron, tipoDePadron).split(' - ');
  const padron_nombre_completo = rest.join().replace(',', ' - ');
  const output = {
    ...recibo,
    clave_padron,
    folio: recibo.folio || recibo.id,
    fecha_de_creacion: recibo.fecha_de_creacion,
    tipo_padron: padrones.find((e) => e.id === tipoDePadron)?.descripcion,
    cajero: cajero ? `${numEmpleado} - ${nombreEmpleado}` : null,
    padron_nombre_completo,
    estados_globales: estadosGlobales.find((e) => e.id === recibo.estados_globales)?.descripcion,
    importe_total: currencyFormater(recibo.importe_total),
    canal: canal_de_pago.descripcion,
    observaciones: recibo.estados_globales === ESTADOS_GLOBALES_MAP.CANCELADO
      ? recibo.observacion_de_la_cancelacion : recibo.observaciones,
  };

  return formatReceived(output);
};

/**
 *  Get a varios racibos por ID
 * @param {Array<Number>} recibos
 * @param {Object} config
 * @returns {Promise<Array<Object>>}
 */
export const getManyRecibos = async (recibos = []) => {
  try {
    const promises = recibos.map((r) => API.get(`${baseURI}${r?.id || r}/`));
    const responses = await Promise.all(promises);
    const datos = responses.map((e) => formatValues(e.data));
    return datos;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * Obtener los tickets
 * @param {Array} recibos_id Array de ids
 * @returns {Promise<Object>}
 */
export const getTickets = async (recibos_id = []) => {
  const url = 'recaudacion/recibo-ticket/pdfs';
  const defMessage = 'Obteniendo Tickets...';

  const [data] = await getConsultaTask(
    { recibos_id },
    url,
    defMessage,
    null,
    [],
    true,
    null,
  );
  if (data) {
    const base64 = `data:application/pdf;base64,${data.data}`;
    return { success: true, base64 };
  }
  return { success: false };
};

/**
 * POST Para Facturar Recibos
 * @param {Array<Number>} recibos
 * @param {Object} values
 * @returns {Promise<boolean|Array>}
 */
export const postFacturacion = async (recibos, _values) => {
  try {
    const values = { ..._values };
    values.accionInf = 'GENERAR_FACTURA';
    const promises = recibos.map((recibo) => API
      .post(`${baseURI}${recibo}/facturacion/`, values));
    const responses = await Promise.all(promises);
    const datos = responses.filter((r) => r?.status === 200);
    onSuccess(null, 'Facturación realizada correctamente');
    return datos;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Para agregar observaciones a recibos
 * @param {Array<Number>} recibos
 * @param {Array<Object>} dataSource
 * @returns retorna un array de objetos con los datos de los recibos
 */
export const patchRecibosConObservaciones = async (recibos, dataSource) => {
  try {
    const contenTypesPadron = await getContentTypesPadrones();
    const promises = recibos.map((recibo) => {
      const { nivel } = contenTypesPadron.find((e) => e.id === recibo.content_type_padron);
      const key = `${recibo.object_id_padron}_${nivel}`;
      const { observaciones } = dataSource.find((e) => e.key === key);
      return API.patch(`recaudacion/recibos/${recibo.id}`, { observaciones });
    });
    const responses = await Promise.all(promises);
    const datos = responses.filter((r) => r?.status === 200);
    return datos;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 *
 * @param {Array<Number>} recibos_id
 * @returns {Promise<Array|Object>}
 */
export const getRecibosPDF = async (recibos_id, access) => {
  const url = 'recaudacion/recibo/pdfs';
  const defMessage = 'Obteniendo Recibos...';
  const config = access ? {
    headers: {
      authorization: `Bearer ${access}`,
    },
  } : null;
  const [data] = await getConsultaTask(
    { recibos_id, accionInf: 'CONSULTAR_RECIBO_CAJA' },
    url,
    defMessage,
    null,
    [],
    true,
    config,
  );
  if (data && data.data) {
    const base64 = `data:application/pdf;base64,${data.data}`;
    return { success: true, base64 };
  }
  return { success: false, base64: null };
};

/**
 *
 * @param number id
 * @returns {Promise<Array|Object>}
 */
export const getFacturaPDF = async (id) => {
  try {
    const { status, data } = await API.get(`/recaudacion/recibos/${id}/pdf-factura/`);
    const base64 = `data:application/pdf;base64,${data.pdf}`;
    return { success: status === 200, base64 };
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * post PDF recibo de pago de trámite
 * @param number id
 * @returns {Promise<Array|Object>}
 */
export const getReciboPDFDeTramite = async (cargo, tramite_en_proceso = false) => {
  try {
    const { data: recibo } = await API.post('/recaudacion/tramite/pdfs/', { cargo });
    if (ENV_NAME === 'juarez') {
      const resTicket = await API.post('recaudacion/recibo-ticket/pdfs/', {
        recibos_id: recibo.id,
        entidad_id: 1,
        tramite_en_proceso,
      });
      return { base64: `data:application/pdf;base64,${resTicket.data.data}`, id: recibo.id, folio: recibo.folio };
    }
    return { base64: `data:application/pdf;base64,${recibo.data}`, id: recibo.id, folio: recibo.folio };
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Generar recibos
 * @param {Object} values
 * @returns {Promise<object>}
 */
export const postPagarConvenio = async (values, padron = {}) => {
  try {
    const response = await API.post('/recaudacion/recibos-convenio/', values);
    store.dispatch({
      type: SET_RECIBO_CONVENIO,
      payload: {
        recibos: response.data.recibos,
        padron,
      },
    });
    return { success: true, recibos: response.data.recibos };
  } catch (err) {
    onError(err);
    return { success: false };
  }
};

export const getRecibosNoPaginados = async (params = null) => {
  try {
    const response = await API.get(`${baseURI}`, { params });
    if (!response.data.next) return response.data.results;
    const page = new URL(response.data.next).searchParams.get('page');
    const _cargos = await getRecibosNoPaginados({ ...params, page });
    return formatReceived(response.data.results.concat(_cargos));
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * Obtener los recibos por parcialidades
 * @param {object} data
 * @param {number[]} data.parcialidades
 * @returns {Promise<{recibos: number[], error: string} | { error: string }>}
 */
export const getRecibosPorParcialidades = async (data) => {
  try {
    const response = await API.post('/recaudacion/recibos-convenio/folios_por_parcialidades/', data);
    store.dispatch({
      type: SET_LAST_PAYMENT_DATA_CONVENIO,
      payload: [],
    });
    return { recibos: response.data.recibos.map((e) => e.id) };
  } catch (err) {
    return { error: err.response.data.error };
  }
};

/**
  POST Acreditar Recibos
  @param {Object} values
  @param {Array<Number>} values.cargos
  @param {Number} values.concepto_de_acreditacion
  @param {String} values.observaciones_de_acreditacion
  @returns {Promise<Object>}
*/
export const postReciboAcreditador = async (values) => {
  try {
    const response = await API.post(`${baseURI}acreditaciones/`, values);
    onSuccess(response, 'Acreditación realizada correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
